import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PropTypes } from 'prop-types';
import { useDispatch } from 'react-redux';
import {
    documentApi,
    useDeleteDocumentMutation,
    useInitiateNewDDASignatureMutation,
    useReinitiateDDASignatureMutation,
    useReplaceDocumentMutation,
    useUpdateBankingStatementMutation,
    useUpdateBankingStatementValidationMutation,
    useValidateDocumentMutation,
} from 'api/main';
import clsx from 'clsx';
import moment from 'moment';

import { notificationCreated } from 'reducers/appNotification';
import {
    checkAcceptedFileTypes,
    downloadClick,
    getDocumentName,
    isJumioDoc,
    parseFullName,
    validateFileSize,
} from 'utils/helpers';
import { useSmallScreen, useUserRoleMatchCategory } from 'utils/hooks';
import {
    APPROVABLE_DOCUMENTS,
    bankingStatementStatus,
    docTypes,
    paymentMethods,
    roleCategories,
    validationStatus,
} from 'utils/constants';
import { texts } from 'utils/languages/fr';

import { DocumentStatus } from 'containers/dashboard/common-components';
import JumioReplaceIframe from './JumioReplaceIframe';
import IdReplaceDialog from './IdReplaceDialog';
import RibReplaceDialog from './RibReplaceDialog';
import {
    ActionButton,
    ConfirmDialog,
    CustomButton,
    CustomTextField,
    FileErrorMessage,
    FileInputButton,
    Loader,
    ValidationText,
} from 'components';
import { Box, IconButton, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import * as icons from 'mdi-material-ui';

const useStyles = makeStyles(theme => ({
    replaceJumioDoc: {
        padding: '2px 5px',
        marginLeft: '20px',
        marginRight: '20px',
        width: 'fit-content',
        height: 'fit-content',
        [theme.breakpoints.between(351, 720)]: {
            margin: '10px 0 0 0',
        },
        [theme.breakpoints.down(351)]: {
            marginLeft: '0 !important',
        },
    },
    docTitle: {
        cursor: 'pointer',
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    bold: { fontWeight: '600' },
    docTitleSmall: {
        flexBasis: '50%',
        marginBottom: 5,
    },
    replacedBy: {
        marginLeft: 20,
        fontStyle: 'italic',
    },
    fileName: {
        marginLeft: 20,
        [theme.breakpoints.down(720)]: {
            margin: 0,
        },
    },
    validationStamp: {
        marginLeft: 20,
        fontSize: 15,
        fontStyle: 'italic',
        lineHeight: 1.6,
        [theme.breakpoints.down(720)]: {
            margin: 0,
        },
    },
    replaceButton: {
        padding: '2px 5px !important',
        marginLeft: '20px !important',
        width: 'fit-content',
        height: 'fit-content',
        [theme.breakpoints.down(720)]: {
            margin: '5px 0 5px 0 !important',
        },
    },
    deleteIcon: {
        marginLeft: 10,
        height: 'fit-content',
        marginTop: -3,
    },
    commentField: {
        width: '70%',
        margin: 0,
    },
}));

const actionButtonStyle = {
    borderRadius: '5px',
    padding: '2px 5px',
    marginLeft: '20px',
};

const getPaymentMethodDialogContent = (paymentMethod, hasDDA) => {
    return paymentMethod === paymentMethods.DIRECT_DEBIT ? (
        <Typography>{texts.CHANGE_TO_CREDIT_TRANSFER}</Typography>
    ) : (
        <>
            <Typography>{texts.CHANGE_TO_DIRECT_DEBIT}</Typography>
            <Typography>{hasDDA ? texts.ALREADY_SIGNED_DDA : texts.NEED_TO_SIGN_DDA}</Typography>
        </>
    );
};

const Document = ({
    doc,
    isReplaceable,
    isRemovable,
    canValidate,
    canArchive,
    onChange,
    jumioStatus,
    useJumioIframe,
    onFinishReplace,
    displayValidation,
    fullName,
    boldName,
    belongsToCoSubscriber,
    replaceableRIB,
    withRIBValidation,
    bankingStatement,
    hasDDA,
    isAudit,
}) => {
    const classes = useStyles();
    const isSmallScreen = useSmallScreen('720');
    const [downloadingDoc, setDownloadingDoc] = useState(null);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [fileSizeError, setFileSizeError] = useState(null);
    const [fileTypeError, setFileTypeError] = useState(false);
    const [mimeType, setMimeType] = useState('');
    const [replaceIframeVisible, setReplaceIframeVisible] = useState(false);
    const [showCommentLine, setShowCommentLine] = useState(false);
    const [rejectedComment, setRejectedComment] = useState(doc.validationComment || '');
    const [ongoingValidationStatus, setOngoingValidationStatus] = useState(
        validationStatus.NOT_VALIDATED
    );
    const [idReplaceDialogOpen, setIdReplaceDialogOpen] = useState(false);
    const [replacingDocId, setReplacingDocId] = useState(null);
    const [ribDialogOpen, setRibDialogOpen] = useState(false);
    const [paymentMethodDialogOpen, setPaymentMethodDialogOpen] = useState(false);

    const dispatch = useDispatch();
    const [deleteDocument, { isLoading: removeDocLoader }] = useDeleteDocumentMutation();
    const [
        updateBankingStatementValidation,
        { isLoading: ribValidationLoader, isSuccess: ribValidationSuccess },
    ] = useUpdateBankingStatementValidationMutation();
    const [
        reinitiateDDASignature,
        {
            isLoading: signatureLoader,
            isSuccess: signatureSuccess,
            data: signatureData,
            reset: resetSignature,
        },
    ] = useReinitiateDDASignatureMutation();

    const isJumioType = useMemo(() => isJumioDoc(doc), [doc]);
    const isSecondId = doc.type === docTypes.SECONDARY_ID;
    const isFirstId = doc.type === docTypes.ID;
    const userIsInvestor = useUserRoleMatchCategory(roleCategories.INVESTORS);
    const [
        replaceDocument,
        { isLoading: replaceLoading, isSuccess: replaceSuccess, reset: replaceReset },
    ] = useReplaceDocumentMutation();
    const [validateDocument, { isLoading: validationLoader, isSuccess: validationSuccess }] =
        useValidateDocumentMutation();
    const [updateBankingStatement, { isLoading: bankingLoader, isSuccess: bankingSuccess }] =
        useUpdateBankingStatementMutation();
    const [initiateNewDDA, { isLoading: ddaLoader, isSuccess: ddaSuccess, data: ddaResponse }] =
        useInitiateNewDDASignatureMutation();

    useEffect(() => {
        setMimeType(doc.mimeType || 'application/pdf');
    }, [doc.mimeType]);

    const setNotification = useCallback(
        messageKey => {
            dispatch(notificationCreated(messageKey));
        },
        [dispatch]
    );

    const handleChangePaymentMethod = useCallback(() => {
        if (ddaSuccess && ddaResponse?.signerUrl) {
            window.open(ddaResponse.signerUrl);
            setPaymentMethodDialogOpen(false);
            return;
        }
        const newPaymentMethod =
            bankingStatement?.paymentMethod === paymentMethods.DIRECT_DEBIT
                ? paymentMethods.CREDIT_TRANSFER
                : paymentMethods.DIRECT_DEBIT;
        if (!hasDDA && bankingStatement?.paymentMethod === paymentMethods.CREDIT_TRANSFER) {
            initiateNewDDA(bankingStatement?.id);
        } else {
            updateBankingStatement({ id: bankingStatement?.id, paymentMethod: newPaymentMethod });
        }
    }, [bankingStatement, ddaResponse, ddaSuccess, hasDDA, initiateNewDDA, updateBankingStatement]);

    const handleDownload = useCallback(() => {
        if (!doc.id && doc.url) {
            downloadClick(doc.url);
            return;
        }
        setDownloadingDoc(doc.id);
        documentApi
            .downloadDocumentById(doc.id, isAudit)
            .then(response => {
                const bytes = new Uint8Array(response.data);
                const blob = new Blob([bytes], { type: doc.mimeType || mimeType });
                const url = window.URL.createObjectURL(blob);
                if (
                    doc.mimeType?.includes('application/vnd.openxmlformats-officedocument.word') ||
                    mimeType?.includes('application/vnd.openxmlformats-officedocument.word')
                ) {
                    downloadClick(url, doc.name, true, false);
                } else {
                    downloadClick(url);
                }
                setDownloadingDoc(null);
            })
            .catch(() => {
                setNotification('FAILED_DOCUMENT_DOWNLOAD');
                setDownloadingDoc(null);
            });
    }, [doc, setNotification, mimeType, isAudit]);

    const handleFileChange = useCallback(
        e => {
            const file = e.target.files[0];
            checkAcceptedFileTypes(
                file,
                () => {
                    setFileTypeError(false);
                    if (validateFileSize(file)) {
                        setFileSizeError(false);
                        setUploadedFile(file);
                        const formData = new FormData();
                        formData.append('file', file);
                        replaceDocument({ id: doc.id, formData, updateUser: true });
                    } else {
                        setFileSizeError(true);
                        setUploadedFile(null);
                    }
                },
                () => {
                    setUploadedFile(null);
                    setFileTypeError(true);
                    setFileSizeError(false);
                }
            );
        },
        [replaceDocument, doc.id]
    );

    useEffect(() => {
        if (replaceSuccess) {
            replaceReset();
            onChange?.();
        }
    }, [replaceSuccess, replaceReset, onChange]);

    useEffect(() => {
        if (signatureSuccess) {
            window.open(signatureData.signerUrl);
        }
    }, [signatureData, signatureSuccess]);

    useEffect(() => {
        if (bankingSuccess) {
            setPaymentMethodDialogOpen(false);
        }
    }, [bankingSuccess]);

    useEffect(() => {
        if (ddaSuccess && ddaResponse?.signerUrl) {
            setTimeout(() => {
                window.open(ddaResponse.signerUrl);
                resetSignature();
                setPaymentMethodDialogOpen(false);
            }, 1000);
        }
    }, [ddaResponse, ddaSuccess, resetSignature]);

    const handleRemoveDocument = useCallback(() => {
        deleteDocument(doc.id);
    }, [deleteDocument, doc.id]);

    const handleFinishJumioReplace = useCallback(() => {
        setReplaceIframeVisible(false);
        onFinishReplace?.();
    }, [onFinishReplace]);

    const handleRejectedCommentChange = useCallback(e => {
        const value = e.target.value;
        setRejectedComment(value);
    }, []);

    const handleAcceptDocument = useCallback(() => {
        setOngoingValidationStatus(validationStatus.ACCEPTED);
        const payload = {
            validationStatus: validationStatus.ACCEPTED,
            validationComment: '',
        };
        validateDocument({ id: doc.id, ...payload });
    }, [validateDocument, doc.id]);

    const handleRejectDocument = useCallback(
        e => {
            setOngoingValidationStatus(validationStatus.REJECTED);
            const payload = {
                validationStatus: validationStatus.REJECTED,
                validationComment: rejectedComment,
            };
            validateDocument({ id: doc.id, ...payload });
        },
        [validateDocument, doc.id, rejectedComment]
    );

    const handleOpenIdReplaceDialog = useCallback(
        id => () => {
            setIdReplaceDialogOpen(true);
            setReplacingDocId(id);
        },
        []
    );
    const handleCloseIdReplaceDialog = useCallback(() => {
        setIdReplaceDialogOpen(false);
        setReplacingDocId(null);
    }, []);
    const handleUpdateBankingStatement = useCallback(
        value => () => {
            updateBankingStatementValidation({ value, id: bankingStatement?.id });
        },
        [updateBankingStatementValidation, bankingStatement]
    );
    const handleInitiateAPSignature = useCallback(() => {
        reinitiateDDASignature(bankingStatement?.id);
    }, [reinitiateDDASignature, bankingStatement]);

    const validationButtons = useMemo(() => {
        if (displayValidation) {
            if (doc.validationStatus === validationStatus.ACCEPTED) {
                return (
                    <ValidationText validatedBy={doc.validatedBy} validatedAt={doc.validatedAt} />
                );
            } else if (doc.validationStatus === validationStatus.REJECTED) {
                return (
                    <Typography className={classes.validationStamp}>
                        {`${doc.validationComment} | ${parseFullName(doc.validatedBy)} | ${moment(
                            doc.validatedAt
                        ).format('DD.MM.YYYY')}`}
                    </Typography>
                );
            } else if (
                APPROVABLE_DOCUMENTS.includes(doc.type) &&
                canValidate &&
                (doc.type === docTypes.OTHER ? doc.createdBy?.id === doc.belongsTo?.id : true)
            ) {
                return (
                    <React.Fragment>
                        <CustomButton
                            noMargin
                            id={`accept-document-${doc.id}`}
                            label={texts.COMPLIANT}
                            className={classes.replaceJumioDoc}
                            onClick={handleAcceptDocument}
                            disabled={validationLoader}
                            size="medium"
                            loading={
                                validationLoader &&
                                ongoingValidationStatus === validationStatus.ACCEPTED
                            }
                        />
                        <CustomButton
                            noMargin
                            id={`reject-document-${doc.id}`}
                            label={texts.NON_COMPLIANT}
                            className={classes.replaceJumioDoc}
                            onClick={() => setShowCommentLine(true)}
                            disabled={validationLoader}
                        />
                    </React.Fragment>
                );
            }
        }
    }, [
        classes,
        doc,
        canValidate,
        handleAcceptDocument,
        displayValidation,
        validationLoader,
        ongoingValidationStatus,
    ]);

    useEffect(() => {
        if (ribValidationSuccess) {
            onFinishReplace();
        }
    }, [ribValidationSuccess, onFinishReplace]);

    useEffect(() => {
        if (validationSuccess) {
            setShowCommentLine(false);
        }
    }, [validationSuccess]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', marginBottom: '15px' }}>
            <Box
                sx={theme => ({
                    display: 'flex',
                    flexWrap: isJumioDoc ? 'wrap' : 'noWrap',
                    alignItems: 'center',
                    [theme.breakpoints.down(720)]: {
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                    },
                })}
            >
                <Loader loading={!!downloadingDoc} size={15} marginTop={0}>
                    <Typography
                        key={doc.id}
                        className={clsx({
                            [classes.docTitle]: true,
                            [classes.bold]: boldName,
                            [classes.docTitleSmall]: !!uploadedFile && !replaceLoading,
                        })}
                        onClick={replaceLoading ? undefined : handleDownload}
                        component="span"
                    >
                        {getDocumentName(doc, fullName, belongsToCoSubscriber)}
                    </Typography>
                </Loader>
                {isReplaceable && uploadedFile && !replaceLoading && (
                    <Typography
                        className={classes.fileName}
                        gutterBottom
                    >{`${texts.UPLOADED_FILE_NAME} : ${uploadedFile.name}`}</Typography>
                )}
                {withRIBValidation && (
                    <React.Fragment>
                        {bankingStatement?.status === bankingStatementStatus.PENDING_SIGNATURE && (
                            <ValidationText
                                validatedAt={bankingStatement?.validatedAt}
                                validatedBy={bankingStatement?.validatedBy}
                            />
                        )}
                        {bankingStatement?.status === bankingStatementStatus.PENDING_VALIDATION && (
                            <Loader loading={ribValidationLoader} marginTop={0} pl={2} size={20}>
                                <ActionButton
                                    variant="outlined"
                                    sx={actionButtonStyle}
                                    onClick={handleUpdateBankingStatement(true)}
                                >
                                    {texts.VALIDATE_CHANGE}
                                </ActionButton>
                                <ActionButton
                                    variant="outlined"
                                    sx={actionButtonStyle}
                                    onClick={handleUpdateBankingStatement(false)}
                                >
                                    {texts.INVALIDATE_CHANGE}
                                </ActionButton>
                            </Loader>
                        )}
                    </React.Fragment>
                )}
                {replaceableRIB && (
                    <React.Fragment>
                        <Box>
                            {bankingStatement?.status ===
                                bankingStatementStatus.PENDING_VERIFICATION && (
                                <Typography className={classes.validationStamp}>
                                    {texts.IBAN_VERIFICATION_PENDING}
                                </Typography>
                            )}
                            {bankingStatement?.status ===
                                bankingStatementStatus.PENDING_VALIDATION && (
                                <Typography className={classes.validationStamp}>
                                    {texts.IBAN_VALIDATION_PENDING}
                                </Typography>
                            )}
                            {bankingStatement?.status ===
                                bankingStatementStatus.PENDING_SIGNATURE && (
                                <ActionButton
                                    variant="outlined"
                                    sx={actionButtonStyle}
                                    onClick={handleInitiateAPSignature}
                                    loading={signatureLoader}
                                >
                                    {texts.SIGN_DIRECT_DEBIT_AUTHORIZATION}
                                </ActionButton>
                            )}
                            {bankingStatement?.status === bankingStatementStatus.OK && (
                                <ActionButton
                                    variant="outlined"
                                    sx={actionButtonStyle}
                                    onClick={() => setRibDialogOpen(true)}
                                >
                                    {texts.REMPLACER}
                                </ActionButton>
                            )}
                            <RibReplaceDialog
                                open={ribDialogOpen}
                                doc={doc}
                                onClose={() => setRibDialogOpen(false)}
                                bankingStatement={bankingStatement}
                            />
                        </Box>
                        <Box display="flex" alignItems="center" flexBasis="100%">
                            <Typography className={classes.validationStamp}>
                                {bankingStatement?.paymentMethod === paymentMethods.DIRECT_DEBIT
                                    ? texts.YOU_CHOSE_DIRECT_DEBIT
                                    : texts.YOU_CHOSE_CREDIT_TRANSFER}
                            </Typography>
                            {bankingStatement?.status === bankingStatementStatus.OK && (
                                <ActionButton
                                    variant="outlined"
                                    sx={actionButtonStyle}
                                    onClick={() => setPaymentMethodDialogOpen(true)}
                                >
                                    {texts.EDIT}
                                </ActionButton>
                            )}
                            <ConfirmDialog
                                open={paymentMethodDialogOpen}
                                title={texts.PAYMENT_METHOD_DIALOG_TITLE}
                                handleClose={() => setPaymentMethodDialogOpen(false)}
                                handleConfirm={handleChangePaymentMethod}
                                loading={bankingLoader || ddaLoader}
                            >
                                {getPaymentMethodDialogContent(
                                    bankingStatement?.paymentMethod,
                                    hasDDA
                                )}
                            </ConfirmDialog>
                        </Box>
                    </React.Fragment>
                )}
                {(isReplaceable || canArchive) &&
                    !isSecondId &&
                    (!isJumioType ? (
                        <Loader
                            loading={replaceLoading}
                            marginTop={0}
                            pl={2}
                            alignItems="flex-start"
                            size={20}
                        >
                            <FileInputButton
                                noMargin
                                id={`replace-button-${doc.id}`}
                                label={canArchive ? texts.REPLACE_AND_ARCHIVE : texts.REMPLACER}
                                onFileChange={handleFileChange}
                                buttonClassName={classes.replaceButton}
                            />
                            {fileSizeError && <FileErrorMessage variant="size" />}
                            {fileTypeError && <FileErrorMessage variant="type" />}
                        </Loader>
                    ) : (
                        <CustomButton
                            noMargin
                            id={`replace-button-${doc.id}`}
                            label={canArchive ? texts.REPLACE_AND_ARCHIVE : texts.REMPLACER}
                            className={classes.replaceJumioDoc}
                            onClick={
                                isFirstId && !userIsInvestor && !useJumioIframe
                                    ? handleOpenIdReplaceDialog(doc.id)
                                    : () => setReplaceIframeVisible(true)
                            }
                        />
                    ))}
                <IdReplaceDialog
                    open={idReplaceDialogOpen}
                    id={replacingDocId}
                    handleClose={handleCloseIdReplaceDialog}
                />
                {validationButtons}
                {isReplaceable && isJumioType && <DocumentStatus status={jumioStatus} />}
                {isAudit && (
                    <Typography className={classes.replacedBy}>
                        {texts.REPLACED_ON_BY(
                            moment(doc.createdAt).format('DD/MM/YYYY'),
                            doc.createdByName
                        )}
                    </Typography>
                )}
                {isRemovable &&
                    (!isSmallScreen ? (
                        <IconButton
                            disableRipple
                            size="small"
                            id={`remove-button-${doc.id}`}
                            onClick={handleRemoveDocument}
                            className={classes.deleteIcon}
                            disabled={removeDocLoader}
                        >
                            <icons.Delete />
                        </IconButton>
                    ) : (
                        <CustomButton
                            noMargin
                            id={`remove-button-${doc.id}`}
                            label={texts.SUPPRIMER}
                            className={classes.replaceButton}
                            onClick={handleRemoveDocument}
                            disabled={removeDocLoader}
                            loading={removeDocLoader}
                        />
                    ))}
            </Box>
            <Box display="flex" flexDirection="column">
                {replaceIframeVisible && (
                    <JumioReplaceIframe doc={doc} onFinish={handleFinishJumioReplace} />
                )}
                {showCommentLine && (
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        gap={2}
                        paddingY={1}
                    >
                        <CustomTextField
                            inputStyle={classes.commentField}
                            value={rejectedComment}
                            onChange={handleRejectedCommentChange}
                        />
                        <CustomButton
                            noMargin
                            label={texts.SAVE_AND_CONTINUE}
                            sx={{ flex: '1 0' }}
                            onClick={handleRejectDocument}
                            disabled={validationLoader}
                            loading={validationLoader}
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

Document.propTypes = {
    doc: PropTypes.object,
    isReplaceable: PropTypes.bool,
    canArchive: PropTypes.bool,
    boldName: PropTypes.bool,
    onChange: PropTypes.func,
    jumioStatus: PropTypes.object,
    useJumioIframe: PropTypes.bool,
    onFinishReplace: PropTypes.func,
    displayValidation: PropTypes.bool,
    belongsToCoSubscriber: PropTypes.bool,
    replaceableRIB: PropTypes.bool,
    withRIBValidation: PropTypes.bool,
    bankingStatement: PropTypes.object,
    isRemovable: PropTypes.bool,
    canValidate: PropTypes.bool,
    hasDDA: PropTypes.bool,
    isAudit: PropTypes.bool,
    fullName: PropTypes.string,
};

export default Document;
